import { css } from "styled-components";

/**
 * src/components/productCardFooter.js
 * ---------------------------------------------------------------------------
 * The price-and-buttons row at the bottom of a product card, for every
 * storefront that has one.
 *
 * THE BUG THESE RULES FIX, because it is not obvious from looking at the CSS:
 * on VendorShopPage the footer was a flex row holding a price group with
 * `min-width: 0; flex: 1 1 auto` and a price span with `white-space: nowrap`.
 * Those two are a trap together. `min-width: 0` lets the flex item shrink
 * below its own content, `nowrap` stops the content shrinking with it, so the
 * price OVERFLOWS its box to the right. The action buttons come later in the
 * DOM, so they paint on top of it. Measured on a 360px phone: the price span
 * needed 157.5px in a 127px box and the buttons covered 112px of it. A rider
 * of this page sees "UGX 32,000" struck through, then a green "U", then a
 * cart button where the real price should be.
 *
 * The arithmetic also rules out a one-line answer. A card on a 360px screen
 * has 127px of content width; a sale price row needs about 157px and three
 * 32-40px buttons need about 114px. They cannot share a line at any font size
 * worth reading, so on a phone the price takes its own row and the buttons sit
 * right-aligned underneath.
 *
 * Verified in a headless browser at 320, 360, 390, 414, 768 and 1024px: the
 * price never leaves its card and never intersects the buttons, and desktop is
 * unchanged, because a wrapping flex row only wraps when it has to.
 *
 * Three storefronts had the same footer shape copied into them, which is why
 * these live here rather than in any one page.
 */

/** The footer row itself: PriceAndButtonContainer, ProductFooter, whatever a
 *  given page calls it. Lets the row break instead of stacking on itself. */
export const cardFooterRow = css`
  flex-wrap: wrap;
  row-gap: 8px;
`;

/**
 * The price span, which is `component` for <PriceCut/> and therefore holds the
 * struck list price and the sale price as two separate runs. As a wrapping
 * flex box it can break BETWEEN those two and nowhere else, so each figure
 * stays whole. Keep `white-space: nowrap` alongside this where a page has it:
 * the nowrap protects the digits, the flex wrap provides the break.
 */
export const cardPriceRow = css`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 0;
  row-gap: 2px;
  min-width: 0;
  max-width: 100%;
`;

/** On a phone the price gets the whole row to itself. */
export const cardPriceOwnsItsRow = css`
  @media (max-width: 768px) {
    flex: 1 1 100%;
  }
`;

/** The buttons, right-aligned whether they share the row or wrap below it. */
export const cardActionsRow = css`
  margin-left: auto;
  flex: 0 0 auto;
`;

const productCardFooter = {
  cardFooterRow,
  cardPriceRow,
  cardPriceOwnsItsRow,
  cardActionsRow,
};

export default productCardFooter;
